import { ForteXApiError, isAbortError, messageOf } from "./errors";
import { openLayerEvents } from "./live";
import type { LayerEvents } from "./live";
import type {
  AddApiKeyUserRequest,
  AddUserRequest,
  ApiKeyDto,
  ApiKeyIssue,
  AddressHit,
  KeyValue,
  LayerChange,
  LayerData,
  LayersRefreshResult,
  LayerTablePage,
  LayerTablePosition,
  LoginResponse,
  MapNodeKindName,
  MapObjectList,
  MapObjectQuery,
  MapRootName,
  MapSettingsValidationResult,
  MasterDataModel,
  MasterDataMoveModel,
  MasterDataUpdateModel,
  ObjectDetail,
  ObjectFileGroup,
  ObjectLayerInfo,
  ObjectLocalized,
  ObjectSaveModel,
  PathResult,
  PublicMapDefinition,
  PublicMapDetail,
  PublicMapModel,
  PublicMapUpdate,
  RoleFeatureGrant,
  RoleGrants,
  RoleGrantsUpdate,
  RoleModel,
  TableViewPage,
  TableViewRange,
  TileFeature,
  TileField,
  TileFilterCapped,
  TileFilterField,
  TileFilterHit,
  TileFilterResult,
  TileFilterValues,
  TileHit,
  TrackPoint,
  UpdateUserRequest,
  UploadResult,
  UploadedFile,
  UserDto,
  UserFeatureAccess,
  UserRoles,
  UserRolesUpdate,
  VectorFeatureCollection
} from "./types";
import { pagingQueryString, toGridPage } from "./paging";
import type { EntityCollection, GridPage, GridQuery } from "./paging";

export type ForteXApiOptions = {
  baseUrl: string;
  token?: () => string | null;
  langId?: number;
  fetch?: typeof fetch;
  nominatimUrl?: string | null;
  nominatimCountry?: string | null;
  onUnauthorized?: (error: ForteXApiError) => void;
};

export type RequestInit2 = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  accept?: string;
  headers?: Record<string, string>;
};

export const defaultNominatimUrl = "https://nominatim.openstreetmap.org/search";

export const routes = {
  publicTree: (root: MapRootName) => `map/public/tree/${root}`,
  tree: (root: MapRootName) => `map/tree/${root}`,
  publicIcon: (idName: string, variant?: string | null) => `map/public/layer/${encodeURIComponent(idName)}/icon${variant ? `?variant=${encodeURIComponent(variant)}` : ""}`,
  icon: (idName: string) => `masterdata/${encodeURIComponent(idName)}/icon`,
  publicLayers: "map/public/layers",
  layers: "object/layer",
  publicObjects: "map/public/objects",
  objects: "object/map",
  publicObject: (id: number | string, lang: string) => `map/public/object/${encodeURIComponent(String(id))}/${encodeURIComponent(lang)}`,
  object: (id: number | string) => `object/${encodeURIComponent(String(id))}`,
  objectLocalized: (id: number | string, lang: string) => `object/${encodeURIComponent(String(id))}/${encodeURIComponent(lang)}`,
  objectFiles: (id: number | string) => `object/${encodeURIComponent(String(id))}/files`,
  publicVector: "map/public/vector",
  vector: "map/vector",
  tile: "MapService/GetData?service=wmts&request=gettile&layers={layer}&tilematrix={z}&tilecol={x}&tilerow={y}",
  layerTable: (layer: string) => `MapService/Table/${encodeURIComponent(layer)}`,
  layerTablePosition: (layer: string) => `MapService/Table/${encodeURIComponent(layer)}/position`,
  tableView: (tableName: string) => `TableView/${encodeURIComponent(tableName)}`,
  click: "MapService/Click",
  feature: (id: string) => `MapService/Feature/${encodeURIComponent(id)}`,
  updateFeature: (layer: string, id: number | string) => `MapService/Feature/${encodeURIComponent(layer)}/${encodeURIComponent(String(id))}`,
  fields: "MapService/Fields",
  filter: "MapService/Filter",
  filterFields: "MapService/FilterFields",
  deleteCache: "MapService/DeleteCache",
  tracks: "track/list",
  path: "path/find",
  file: (id: string) => `file/${encodeURIComponent(id)}/download`,
  upload: "file/upload",
  login: "Authentication/login",
  masterData: "masterdata",
  masterDataList: "masterdata/list",
  masterDataItem: (id: number) => `masterdata/${id}`,
  masterDataChildren: (parentIdName: string) => `masterdata/${encodeURIComponent(parentIdName)}/children`,
  adminTree: (root: MapRootName) => `map/admin/tree/${root}`,
  adminValidate: "map/admin/validate",
  layerData: (masterDataId: number) => `map/admin/layer/${masterDataId}`,
  refreshLayerData: (masterDataId: number) => `map/admin/layer/${masterDataId}/refresh`,
  refreshLayers: (root: MapRootName) => `map/admin/layers/${root}/refresh`,
  layerHub: "hubs/layers",
  featureAccess: (userId: string) => `user/${encodeURIComponent(userId)}/featureAccess`,
  changePassword: "user/changePassword",
  users: "admin/users",
  user: (id: string) => `admin/users/${encodeURIComponent(id)}`,
  userActivate: (id: string) => `admin/users/${encodeURIComponent(id)}/activate`,
  userRole: (id: string) => `admin/users/${encodeURIComponent(id)}/role`,
  apiKeyUsers: "admin/users/api-key",
  userApiKeys: (id: string) => `admin/users/${encodeURIComponent(id)}/api-keys`,
  rollUserApiKey: (id: string, graceMinutes: number) => `admin/users/${encodeURIComponent(id)}/api-keys/roll?graceMinutes=${graceMinutes}`,
  userApiKey: (id: string, keyId: string) => `admin/users/${encodeURIComponent(id)}/api-keys/${encodeURIComponent(keyId)}`,
  roles: "admin/roles",
  role: (id: number) => `admin/roles/${id}`,
  userRoles: (userId: string) => `admin/roles/user/${encodeURIComponent(userId)}`,
  publicMapDefinition: (id: string) => `publicmap/public/${encodeURIComponent(id)}`,
  publicMaps: "publicmap/list",
  publicMap: (id: string) => `publicmap/${encodeURIComponent(id)}`,
  publicMapCreate: "publicmap",
  publicMapFeatures: "publicmap/features"
};

function coordinate(value: number): string {
  return value.toFixed(7);
}

export const filterCappedHeader = "X-Filter-Capped";

export function filterCapped(header: string | null): TileFilterCapped[] {
  if (header === null) return [];

  return header.split(",").flatMap(part => {
    const trimmed = part.trim();
    const colon = trimmed.lastIndexOf(":");

    if (colon <= 0) return [];

    const layer = trimmed.slice(0, colon).trim();
    const count = trimmed.slice(colon + 1).trim();
    const maxResults = /^\d+$/.test(count) ? Number(count) : 0;

    return layer.length > 0 && maxResults > 0 ? [{ layer, maxResults }] : [];
  });
}

export function filledFilterValues(filters: TileFilterValues): TileFilterValues {
  const result: TileFilterValues = {};

  for (const [layer, values] of Object.entries(filters)) {
    const filled = Object.fromEntries(
      Object.entries(values)
        .map(([key, value]) => [key, value.trim()] as const)
        .filter(([, value]) => value.length > 0)
    );

    if (Object.keys(filled).length > 0) result[layer] = filled;
  }

  return result;
}

export function stripBearerPrefix(value: string): string {
  return typeof value === "string" && value.toLowerCase().startsWith("bearer ") ? value.slice(7).trim() : value;
}

export class ForteXApi {
  readonly baseUrl: string;
  readonly nominatimUrl: string | null;
  readonly nominatimCountry: string;

  private readonly tokenProvider: () => string | null;
  private readonly fetcher: typeof fetch;
  private readonly onUnauthorized: ((error: ForteXApiError) => void) | null;
  private langIdValue: number;
  private readonly blobUrls = new Set<string>();
  private readonly iconCache = new Map<string, Promise<string | null>>();
  private disposed = false;

  constructor(options: ForteXApiOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.tokenProvider = options.token ?? (() => null);
    this.fetcher = options.fetch ?? ((input, init) => fetch(input, init));
    this.langIdValue = options.langId ?? 1;
    this.nominatimUrl = options.nominatimUrl === undefined ? defaultNominatimUrl : options.nominatimUrl;
    this.nominatimCountry = options.nominatimCountry ?? "hu";
    this.onUnauthorized = options.onUnauthorized ?? null;
  }

  get langId(): number {
    return this.langIdValue;
  }

  setLangId(langId: number): void {
    this.langIdValue = langId;
  }

  token(): string | null {
    return this.tokenProvider();
  }

  get authenticated(): boolean {
    return Boolean(this.token());
  }

  url(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;

    return `${this.baseUrl}/${path.replace(/^\/+/, "")}`;
  }

  tileUrlTemplate(layerIdName: string, noCache = false): string {
    const template = this.url(routes.tile.replace("{layer}", encodeURIComponent(layerIdName)));

    return noCache ? `${template}&nocache=true` : template;
  }

  fileUrl(fileId: string): string {
    return this.url(routes.file(fileId));
  }

  iconUrl(idName: string, variant?: "selected" | "hovered" | null): string {
    return this.url(this.authenticated ? routes.icon(idName) : routes.publicIcon(idName, variant));
  }

  async request<T>(path: string, init: RequestInit2 = {}): Promise<T> {
    return (await this.requestWithHeaders<T>(path, init)).payload;
  }

  private async requestWithHeaders<T>(path: string, init: RequestInit2 = {}): Promise<{ payload: T; headers: Headers }> {
    const headers: Record<string, string> = { Accept: init.accept ?? "application/json", ...(init.headers ?? {}) };
    const token = this.token();

    if (init.body !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;

    return this.exchange<T>(path, { method: init.method ?? "GET", headers, body: init.body === undefined ? undefined : JSON.stringify(init.body), signal: init.signal });
  }

  async uploadFiles(files: File[], active = true): Promise<UploadedFile[]> {
    const form = new FormData();

    for (const file of files) form.append("file", file, file.name);

    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.token();

    if (token) headers.Authorization = `Bearer ${token}`;

    const result = await this.send<UploadResult | null>(`${routes.upload}?active=${active ? "true" : "false"}`, { method: "POST", headers, body: form });

    return result?.files ?? [];
  }

  private async send<T>(path: string, init: RequestInit): Promise<T> {
    return (await this.exchange<T>(path, init)).payload;
  }

  private async exchange<T>(path: string, init: RequestInit): Promise<{ payload: T; headers: Headers }> {
    let response: Response;
    let text: string;

    try {
      response = await this.fetcher(this.url(path), init);
      text = await response.text();
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new ForteXApiError(0, "network", error);
    }

    let payload: unknown = null;

    if (text.length > 0) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = text;
      }
    }

    if (!response.ok) {
      const error = new ForteXApiError(response.status, messageOf(response.status, payload), payload);

      if (error.isUnauthorized && this.onUnauthorized) this.onUnauthorized(error);

      throw error;
    }

    return { payload: payload as T, headers: response.headers };
  }

  async blob(path: string, signal?: AbortSignal): Promise<Blob | null> {
    const headers: Record<string, string> = {};
    const token = this.token();

    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;

    try {
      response = await this.fetcher(this.url(path), { headers, signal });
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new ForteXApiError(0, "network", error);
    }

    if (response.status === 404 || response.status === 204) return null;
    if (!response.ok) throw new ForteXApiError(response.status, `HTTP ${response.status}`, null);

    return response.blob();
  }

  async blobUrl(path: string, signal?: AbortSignal): Promise<string | null> {
    const blob = await this.blob(path, signal);

    if (blob === null || blob.size === 0 || this.disposed) return null;

    const url = URL.createObjectURL(blob);

    this.blobUrls.add(url);

    return url;
  }

  typeIcon(idName: string): Promise<string | null> {
    let pending = this.iconCache.get(idName);

    if (pending === undefined) {
      pending = this.blobUrl(this.authenticated ? routes.icon(idName) : routes.publicIcon(idName)).catch(() => null);
      this.iconCache.set(idName, pending);
    }

    return pending;
  }

  tree(root: MapRootName, signal?: AbortSignal): Promise<MasterDataModel[]> {
    return this.request<MasterDataModel[]>(this.authenticated ? routes.tree(root) : routes.publicTree(root), { signal });
  }

  publicTree(root: MapRootName, signal?: AbortSignal): Promise<MasterDataModel[]> {
    return this.request<MasterDataModel[]>(routes.publicTree(root), { signal });
  }

  layers(signal?: AbortSignal): Promise<ObjectLayerInfo[]> {
    const path = this.authenticated ? routes.layers : routes.publicLayers;

    return this.request<ObjectLayerInfo[]>(`${path}?langId=${this.langIdValue}`, { signal });
  }

  mapObjects(query: MapObjectQuery, signal?: AbortSignal): Promise<MapObjectList> {
    const parts = [`typeIds=${encodeURIComponent(query.typeIds.join(";"))}`, `doGroup=${query.group ? "true" : "false"}`, `langId=${this.langIdValue}`];

    if (query.bounds !== null) {
      parts.push(`nwLat=${coordinate(query.bounds.nwLat)}`);
      parts.push(`nwLon=${coordinate(query.bounds.nwLon)}`);
      parts.push(`seLat=${coordinate(query.bounds.seLat)}`);
      parts.push(`seLon=${coordinate(query.bounds.seLon)}`);
    }

    if (query.zoomLevel !== null) parts.push(`zoomLevel=${Math.round(query.zoomLevel)}`);
    if (query.search) parts.push(`search=${encodeURIComponent(query.search)}`);
    if (this.authenticated && (query.publishable === true || query.publishable === false)) parts.push(`publishable=${query.publishable ? "true" : "false"}`);
    if (this.authenticated && (query.showOnMap === true || query.showOnMap === false)) parts.push(`showOnMap=${query.showOnMap ? "true" : "false"}`);
    if (query.take) parts.push(`paging=${encodeURIComponent(`Skip=0&Take=${query.take}&IsAnd=true&Sorting.OrderBy=ASC&Sorting.Field=Id`)}`);

    const path = this.authenticated ? routes.objects : routes.publicObjects;

    return this.request<MapObjectList>(`${path}?${parts.join("&")}`, { signal });
  }

  object(id: number, signal?: AbortSignal): Promise<ObjectDetail> {
    return this.request<ObjectDetail>(routes.object(id), { signal });
  }

  objectLocalized(id: number, lang: string, signal?: AbortSignal): Promise<ObjectLocalized> {
    return this.request<ObjectLocalized>(this.authenticated ? routes.objectLocalized(id, lang) : routes.publicObject(id, lang), { signal });
  }

  objectFiles(id: number, signal?: AbortSignal): Promise<ObjectFileGroup[]> {
    return this.request<ObjectFileGroup[]>(routes.objectFiles(id), { signal });
  }

  saveObject(model: ObjectSaveModel, id?: number | null): Promise<ObjectDetail> {
    return id === null || id === undefined
      ? this.request<ObjectDetail>("object", { method: "POST", body: model })
      : this.request<ObjectDetail>(routes.object(id), { method: "PUT", body: model });
  }

  deleteObject(id: number): Promise<void> {
    return this.request<void>(routes.object(id), { method: "DELETE" });
  }

  vector(node: string, bbox: [number, number, number, number], zoom: number, signal?: AbortSignal): Promise<VectorFeatureCollection> {
    const path = this.authenticated ? routes.vector : routes.publicVector;
    const box = bbox.map(a => a.toFixed(6)).join(",");

    return this.request<VectorFeatureCollection>(`${path}?node=${encodeURIComponent(node)}&bbox=${box}&zoom=${Math.round(zoom)}&langId=${this.langIdValue}`, { signal });
  }

  click(lat: number, lon: number, zoomLevel: number, layers: string[], signal?: AbortSignal): Promise<TileHit[]> {
    const query = `lat=${coordinate(lat)}&lon=${coordinate(lon)}&zoomLevel=${Math.round(zoomLevel)}&layers=${encodeURIComponent(layers.join(","))}`;

    return this.request<KeyValue[]>(`${routes.click}?${query}`, { signal }).then(list => (list ?? []).map(a => ({ id: a.key, name: a.value ?? "" })));
  }

  feature(id: string, layer: string, signal?: AbortSignal): Promise<TileFeature> {
    return this.request<TileFeature>(`${routes.feature(id)}?layer=${encodeURIComponent(layer)}`, { signal });
  }

  featureAt(id: string, lat: number, lon: number, zoomLevel: number, layers: string[], signal?: AbortSignal): Promise<TileFeature> {
    const query = `lat=${coordinate(lat)}&lon=${coordinate(lon)}&zoomLevel=${Math.round(zoomLevel)}&layers=${encodeURIComponent(layers.join(","))}`;

    return this.request<TileFeature>(`${routes.feature(id)}?${query}`, { signal });
  }

  updateFeature(layer: string, id: number | string, data: KeyValue[]): Promise<TileFeature> {
    return this.request<TileFeature>(routes.updateFeature(layer, id), { method: "POST", body: data });
  }

  fields(layers: string[], signal?: AbortSignal): Promise<{ key: string; value: TileField[] }[]> {
    return this.request<{ key: string; value: TileField[] }[]>(`${routes.fields}?layers=${encodeURIComponent(layers.join(","))}`, { signal });
  }

  async filter(term: string, layers: string[], fieldName = "", admin = false, signal?: AbortSignal, filters: TileFilterValues | null = null): Promise<TileFilterResult> {
    const query = `filter=${encodeURIComponent(term)}&layers=${encodeURIComponent(layers.join(","))}&fieldName=${encodeURIComponent(fieldName)}&admin=${admin ? "true" : "false"}`;
    const filled = filters === null ? {} : filledFilterValues(filters);
    const extra = Object.keys(filled).length === 0 ? "" : `&filters=${encodeURIComponent(JSON.stringify(filled))}`;
    const { payload, headers } = await this.requestWithHeaders<TileFilterHit[] | null>(`${routes.filter}?${query}${extra}`, { signal });

    return { hits: payload ?? [], capped: filterCapped(headers.get(filterCappedHeader)) };
  }

  filterFields(layers: string[], signal?: AbortSignal): Promise<TileFilterField[]> {
    return this.request<TileFilterField[]>(`${routes.filterFields}?layers=${encodeURIComponent(layers.join(","))}`, { signal });
  }

  deleteCache(layers: string[]): Promise<void> {
    return this.request<void>(`${routes.deleteCache}?layers=${encodeURIComponent(layers.join(","))}`, { method: "POST" });
  }

  tracks(objectId: number, fromDate?: Date | null, toDate?: Date | null, signal?: AbortSignal): Promise<TrackPoint[]> {
    const parts = [`objectId=${objectId}`];

    if (fromDate) parts.push(`fromDate=${encodeURIComponent(fromDate.toISOString())}`);
    if (toDate) parts.push(`toDate=${encodeURIComponent(toDate.toISOString())}`);

    return this.request<TrackPoint[]>(`${routes.tracks}?${parts.join("&")}`, { signal });
  }

  findPath(start: [number, number], goal: [number, number], signal?: AbortSignal): Promise<PathResult> {
    const query = `startLat=${coordinate(start[0])}&startLon=${coordinate(start[1])}&goalLat=${coordinate(goal[0])}&goalLon=${coordinate(goal[1])}`;

    return this.request<PathResult>(`${routes.path}?${query}`, { signal });
  }

  async login(email: string, password: string, signal?: AbortSignal): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>(routes.login, { method: "POST", body: { email, password }, signal });

    return { ...response, bearer: stripBearerPrefix(response.bearer) };
  }

  featureAccess(userId: string, signal?: AbortSignal): Promise<UserFeatureAccess> {
    return this.request<UserFeatureAccess>(routes.featureAccess(userId), { signal });
  }

  changePassword(currentPassword: string, newPassword: string, confirmNewPassword: string): Promise<void> {
    return this.request<void>(routes.changePassword, { method: "POST", body: { currentPassword, newPassword, confirmNewPassword } });
  }

  masterDataList(query: GridQuery, signal?: AbortSignal): Promise<GridPage<MasterDataModel>> {
    return this.request<EntityCollection<MasterDataModel>>(`${routes.masterDataList}?${pagingQueryString(query)}`, { signal }).then(toGridPage);
  }

  layerTable(layer: string, query: GridQuery, signal?: AbortSignal): Promise<LayerTablePage> {
    return this.request<LayerTablePage>(`${routes.layerTable(layer)}?${pagingQueryString(query)}`, { signal });
  }

  tableView(tableName: string, query: GridQuery, range: TableViewRange, signal?: AbortSignal): Promise<TableViewPage> {
    const dates = [range.fromDate.length > 0 ? `&fromDate=${encodeURIComponent(range.fromDate)}` : "", range.toDate.length > 0 ? `&toDate=${encodeURIComponent(range.toDate)}` : ""].join("");

    return this.request<TableViewPage>(`${routes.tableView(tableName)}?${pagingQueryString(query)}${dates}`, { signal });
  }

  async layerTablePosition(layer: string, id: string, query: GridQuery, signal?: AbortSignal): Promise<number | null> {
    const result = await this.request<LayerTablePosition>(`${routes.layerTablePosition(layer)}?id=${encodeURIComponent(id)}&${pagingQueryString(query)}`, { signal });

    return typeof result?.position === "number" ? result.position : null;
  }

  masterData(id: number, signal?: AbortSignal): Promise<MasterDataModel> {
    return this.request<MasterDataModel>(routes.masterDataItem(id), { signal });
  }

  masterDataChildren(parentIdName: string, signal?: AbortSignal): Promise<MasterDataModel[]> {
    return this.request<MasterDataModel[]>(routes.masterDataChildren(parentIdName), { signal });
  }

  createMasterData(model: MasterDataUpdateModel): Promise<MasterDataModel> {
    return this.request<MasterDataModel>(routes.masterData, { method: "POST", body: model });
  }

  updateMasterData(id: number, model: MasterDataUpdateModel): Promise<MasterDataModel> {
    return this.request<MasterDataModel>(routes.masterDataItem(id), { method: "PUT", body: model });
  }

  deleteMasterData(id: number): Promise<void> {
    return this.request<void>(routes.masterDataItem(id), { method: "DELETE" });
  }

  moveTree(root: MapRootName, moves: MasterDataMoveModel[]): Promise<void> {
    return this.request<void>(routes.adminTree(root), { method: "POST", body: moves });
  }

  validateSettings(kind: MapNodeKindName, root: MapRootName, settingsJson: string, signal?: AbortSignal): Promise<MapSettingsValidationResult> {
    return this.request<MapSettingsValidationResult>(routes.adminValidate, { method: "POST", body: { kind, root, settingsJson }, signal });
  }

  layerData(masterDataId: number, signal?: AbortSignal): Promise<LayerData> {
    return this.request<LayerData>(routes.layerData(masterDataId), { signal });
  }

  refreshLayerData(masterDataId: number): Promise<LayerData> {
    return this.request<LayerData>(routes.refreshLayerData(masterDataId), { method: "POST" });
  }

  refreshLayers(root: MapRootName): Promise<LayersRefreshResult> {
    return this.request<LayersRefreshResult>(routes.refreshLayers(root), { method: "POST" });
  }

  connectLayerEvents(handler: (change: LayerChange) => void): Promise<LayerEvents> {
    return openLayerEvents(this.url(routes.layerHub), () => this.token(), handler);
  }

  users(status?: string | null, search?: string | null, signal?: AbortSignal): Promise<UserDto[]> {
    const parts: string[] = [];

    if (status) parts.push(`status=${encodeURIComponent(status)}`);
    if (search) parts.push(`search=${encodeURIComponent(search)}`);

    return this.request<UserDto[]>(parts.length > 0 ? `${routes.users}?${parts.join("&")}` : routes.users, { signal });
  }

  user(id: string, signal?: AbortSignal): Promise<UserDto> {
    return this.request<UserDto>(routes.user(id), { signal });
  }

  createUser(request: AddUserRequest): Promise<UserDto> {
    return this.request<UserDto>(routes.users, { method: "POST", body: request });
  }

  updateUser(id: string, request: UpdateUserRequest): Promise<UserDto> {
    return this.request<UserDto>(routes.user(id), { method: "PUT", body: request });
  }

  activateUser(id: string, active: boolean): Promise<UserDto> {
    return this.request<UserDto>(routes.userActivate(id), { method: "PUT", body: { active } });
  }

  setUserAdmin(id: string, admin: boolean): Promise<UserDto> {
    return this.request<UserDto>(routes.userRole(id), { method: "PUT", body: { admin } });
  }

  deleteUser(id: string): Promise<void> {
    return this.request<void>(routes.user(id), { method: "DELETE" });
  }

  createApiKeyUser(request: AddApiKeyUserRequest): Promise<ApiKeyIssue> {
    return this.request<ApiKeyIssue>(routes.apiKeyUsers, { method: "POST", body: request });
  }

  userApiKeys(userId: string, signal?: AbortSignal): Promise<ApiKeyDto[]> {
    return this.request<ApiKeyDto[]>(routes.userApiKeys(userId), { signal });
  }

  rollUserApiKey(userId: string, graceMinutes: number): Promise<ApiKeyIssue> {
    return this.request<ApiKeyIssue>(routes.rollUserApiKey(userId, graceMinutes), { method: "POST" });
  }

  revokeUserApiKey(userId: string, keyId: string): Promise<void> {
    return this.request<void>(routes.userApiKey(userId, keyId), { method: "DELETE" });
  }

  resetPassword(userId: string, newPassword: string): Promise<void> {
    return this.request<void>("user/resetPw", { method: "POST", body: { userId, newPassword } });
  }

  roles(signal?: AbortSignal): Promise<RoleModel[]> {
    return this.request<RoleModel[]>(routes.roles, { signal });
  }

  roleGrants(roleId: number, signal?: AbortSignal): Promise<RoleGrants> {
    return this.request<RoleGrants>(routes.role(roleId), { signal });
  }

  setRoleGrants(roleId: number, model: RoleGrantsUpdate): Promise<RoleGrants> {
    return this.request<RoleGrants>(routes.role(roleId), { method: "PUT", body: model });
  }

  userRoles(userId: string, signal?: AbortSignal): Promise<UserRoles> {
    return this.request<UserRoles>(routes.userRoles(userId), { signal });
  }

  setUserRoles(userId: string, model: UserRolesUpdate): Promise<UserRoles> {
    return this.request<UserRoles>(routes.userRoles(userId), { method: "PUT", body: model });
  }

  publicMapDefinition(id: string, signal?: AbortSignal): Promise<PublicMapDefinition> {
    return this.request<PublicMapDefinition>(routes.publicMapDefinition(id), { signal });
  }

  publicMaps(query: GridQuery, signal?: AbortSignal): Promise<GridPage<PublicMapModel>> {
    return this.request<EntityCollection<PublicMapModel>>(`${routes.publicMaps}?${pagingQueryString(query)}`, { signal }).then(toGridPage);
  }

  publicMap(id: string, signal?: AbortSignal): Promise<PublicMapDetail> {
    return this.request<PublicMapDetail>(routes.publicMap(id), { signal });
  }

  publicMapFeatures(signal?: AbortSignal): Promise<RoleFeatureGrant[]> {
    return this.request<RoleFeatureGrant[]>(routes.publicMapFeatures, { signal });
  }

  createPublicMap(model: PublicMapUpdate): Promise<PublicMapDetail> {
    return this.request<PublicMapDetail>(routes.publicMapCreate, { method: "POST", body: model });
  }

  updatePublicMap(id: string, model: PublicMapUpdate): Promise<PublicMapDetail> {
    return this.request<PublicMapDetail>(routes.publicMap(id), { method: "PUT", body: model });
  }

  deletePublicMap(id: string): Promise<void> {
    return this.request<void>(routes.publicMap(id), { method: "DELETE" });
  }

  async searchAddress(term: string, limit = 8, signal?: AbortSignal): Promise<AddressHit[]> {
    if (this.nominatimUrl === null) return [];

    const query = `q=${encodeURIComponent(term)}&format=jsonv2&limit=${limit}&addressdetails=0&countrycodes=${encodeURIComponent(this.nominatimCountry)}`;
    let response: Response;

    try {
      response = await this.fetcher(`${this.nominatimUrl}?${query}`, { headers: { Accept: "application/json" }, signal });
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new ForteXApiError(0, "network", error);
    }

    if (!response.ok) throw new ForteXApiError(response.status, `HTTP ${response.status}`, null);

    const list = (await response.json()) as { display_name?: string; lat?: string; lon?: string; boundingbox?: string[]; category?: string; type?: string }[];

    return list
      .map(a => {
        const lat = Number(a.lat);
        const lon = Number(a.lon);
        const box = a.boundingbox?.map(Number);

        return {
          displayName: a.display_name ?? "",
          lat,
          lon,
          boundingBox: box && box.length === 4 && box.every(Number.isFinite) ? ([[box[0], box[2]], [box[1], box[3]]] as [[number, number], [number, number]]) : null,
          category: a.type ?? a.category ?? null
        };
      })
      .filter(a => Number.isFinite(a.lat) && Number.isFinite(a.lon));
  }

  dispose(): void {
    this.disposed = true;

    for (const url of this.blobUrls) URL.revokeObjectURL(url);

    this.blobUrls.clear();
    this.iconCache.clear();
  }
}

export function createForteXApi(options: ForteXApiOptions): ForteXApi {
  return new ForteXApi(options);
}
